import { useMutation, useQuery } from "@apollo/client";
import { AppContext } from "App";
import { ORIGIN_COUNTRIES } from "AppSettings";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import moment from "moment";
import { useContext, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EDIT_CERTIFICATE } from "utils/permissions";
import { getOriginalFileName } from "utils/string";
import {
  DELETE_LAB_TEST_REPORT,
  FETCH_COMPLIANCE_STANDARDS,
  FETCH_LAB_TEST_REPORTS,
  PAGE_SIZE,
} from "./graphql";

const STATUS_FILTER = {
  ALL: "ALL",
  EXPIRED: "Expired",
  EXPIRING_SOON: "Expiring soon",
  NOT_EXPIRED: "Not expired",
};

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "-";

const formatCountry = (countryCode) => {
  if (!countryCode) return "-";
  const country = ORIGIN_COUNTRIES.find((item) => item.code === countryCode);
  if (!country) return countryCode;
  return `${country.emoji} ${country.name}`;
};

const getStatusDisplay = (expiryStatus) => {
  if (expiryStatus === STATUS_FILTER.EXPIRED) {
    return { emoji: "🔴", text: STATUS_FILTER.EXPIRED };
  }
  if (expiryStatus === STATUS_FILTER.EXPIRING_SOON) {
    return { emoji: "🟡", text: STATUS_FILTER.EXPIRING_SOON };
  }
  if (expiryStatus === STATUS_FILTER.NOT_EXPIRED) {
    return { emoji: "🟢", text: STATUS_FILTER.NOT_EXPIRED };
  }
  return { emoji: "⚪", text: "Unknown" };
};

const LabTestReportsPage = () => {
  const { hasPermission } = useContext(AppContext);
  const canEditLabTestReports = hasPermission(EDIT_CERTIFICATE);
  const [showAllProducts, setShowAllProducts] = useState({});
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams({
    offset: 0,
    q: "",
    country: "",
    status: STATUS_FILTER.ALL,
    standardId: "ALL",
  });
  const params = Object.fromEntries(searchParams.entries());
  const offset = parseInt(params.offset, 10) || 0;
  const q = params.q || "";
  const country = params.country || "";
  const status = params.status || STATUS_FILTER.ALL;
  const standardId = params.standardId || "ALL";

  const { loading, error, data } = useQuery(FETCH_LAB_TEST_REPORTS, {
    variables: {
      offset,
      limit: PAGE_SIZE,
      q,
      country: country || undefined,
      expiryStatus: status === STATUS_FILTER.ALL ? undefined : status,
      standardIds: standardId === "ALL" ? undefined : [standardId],
    },
    fetchPolicy: "network-only",
  });
  const { data: standardsData } = useQuery(FETCH_COMPLIANCE_STANDARDS, {
    fetchPolicy: "cache-first",
  });
  const [deleteLabTestReport, deleteLabTestReportRes] = useMutation(
    DELETE_LAB_TEST_REPORT,
    {
      onCompleted() {
        Alert("success", "Lab test report deleted.");
        setDeletingReportId(null);
      },
      onError(error) {
        Alert("error", parseError(error));
        setDeletingReportId(null);
      },
      refetchQueries: [FETCH_LAB_TEST_REPORTS],
      awaitRefetchQueries: true,
    },
  );

  const reports = data?.labTestReports?.results || [];
  const total = data?.labTestReports?.total || 0;
  const standards = standardsData?.complianceStandards || [];

  function deleteHandler(id) {
    if (!canEditLabTestReports) return;
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this lab test report?",
    );
    if (!shouldDelete) return;

    setDeletingReportId(id);
    deleteLabTestReport({ variables: { id } });
  }

  return (
    <Page
      title="Lab Test Reports"
      className="h-screen"
      rightButtons={
        <div>
          <Link to="new">+ Add Report</Link>
        </div>
      }
    >
      <div className="p-6 h-full flex flex-col">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex-1">
            <SearchBar
              initialQuery={q}
              placeholder="Search by title, report number, issuer..."
              onChange={(nextQ) =>
                setSearchParams({ ...params, q: nextQ, offset: 0 })
              }
            />
          </div>
          <Select
            value={standardId}
            onChange={(e) =>
              setSearchParams({
                ...params,
                standardId: e.target.value,
                offset: 0,
              })
            }
          >
            <option value="ALL">All Standards</option>
            {standards.map((standard) => (
              <option key={standard.id} value={standard.id}>
                {standard.name}
              </option>
            ))}
          </Select>
          <Select
            value={country}
            onChange={(e) =>
              setSearchParams({
                ...params,
                country: e.target.value,
                offset: 0,
              })
            }
          >
            <option value="">All Countries</option>
            {ORIGIN_COUNTRIES.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name} {item.emoji}
              </option>
            ))}
          </Select>
          <Select
            value={status}
            onChange={(e) =>
              setSearchParams({ ...params, status: e.target.value, offset: 0 })
            }
          >
            <option value={STATUS_FILTER.ALL}>All Statuses</option>
            <option value={STATUS_FILTER.EXPIRED}>Expired</option>
            <option value={STATUS_FILTER.EXPIRING_SOON}>Expiring soon</option>
            <option value={STATUS_FILTER.NOT_EXPIRED}>Not expired</option>
          </Select>
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col mt-6">
          <div className="flex-1 overflow-auto relative">
            {loading ? (
              <Spinner />
            ) : error ? (
              <Errors error={error} />
            ) : (
              <div>
                {reports.length === 0 ? (
                  <div className="p-8 text-sm text-gray-500">
                    No lab test reports found.
                  </div>
                ) : (
                  reports.map((report) => {
                    const statusView = getStatusDisplay(report.expiryStatus);
                    return (
                      <div
                        key={report.id}
                        className="border-b border-gray-100 dark:border-gray-700 p-4 md:p-6"
                      >
                        <div className="flex flex-col gap-3">
                          <div>
                            <div className="flex items-center space-x-3 justify-between">
                              <div className="text-xs opacity-60">
                                #{report.reportNumber || "-"}
                              </div>

                              {canEditLabTestReports ? (
                                <Button
                                  trash
                                  loading={
                                    deleteLabTestReportRes.loading &&
                                    deletingReportId === report.id
                                  }
                                  onClick={() => deleteHandler(report.id)}
                                >
                                  Delete
                                </Button>
                              ) : null}
                            </div>

                            <div className="mt-2 flex items-center gap-2">
                              <h5>
                                <Link
                                  className="link"
                                  to={`${report.id}?${searchParams.toString()}`}
                                >
                                  {report.title || "-"}
                                </Link>
                              </h5>
                            </div>
                            <div className="mt-1 text-xs opacity-70">
                              Standards:{" "}
                              {(report.standards || []).length > 0
                                ? report.standards
                                    .map((standard) => standard.name)
                                    .join(", ")
                                : "-"}
                            </div>
                          </div>

                          <div className="text-sm flex flex-wrap gap-x-6 gap-y-1">
                            <div>Country: {formatCountry(report.country)}</div>
                            <div>Issuer: {report.issuer || "-"}</div>
                            <div>
                              Issued Date: 📅 {formatDate(report.issuedDate)}
                            </div>
                            <div>
                              Expire Date: {statusView.emoji}{" "}
                              {report.expiryDate}
                            </div>
                          </div>

                          {report.fileUrls?.length > 0 ? (
                            <div className="text-sm">
                              <div className="flex flex-col items-start gap-1 text-xs">
                                {report.fileUrls.map((fileUrl) => (
                                  <a
                                    key={fileUrl}
                                    className="inline"
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    📎 {getOriginalFileName(fileUrl)}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          <div className="opacity-80">
                            <div>
                              <div className="flex items-center gap-2">
                                <label>
                                  Products ({(report.products || []).length}):{" "}
                                </label>
                                {(report.products || []).length > 5 ? (
                                  <button
                                    type="button"
                                    className="text-xs text-sky-600 hover:text-sky-700"
                                    onClick={() =>
                                      setShowAllProducts((prev) => ({
                                        ...prev,
                                        [report.id]: !prev[report.id],
                                      }))
                                    }
                                  >
                                    {showAllProducts[report.id]
                                      ? "Show less"
                                      : "Show all products"}
                                  </button>
                                ) : null}
                              </div>
                              {(report.products || []).length > 0 ? (
                                <div className="pt-2">
                                  {(showAllProducts[report.id]
                                    ? report.products || []
                                    : (report.products || []).slice(0, 5)
                                  ).map((product) => (
                                    <div
                                      key={product.id}
                                      className="border-t border-gray-100 py-2"
                                    >
                                      <div
                                        className={
                                          !product.isActive && "text-red-600"
                                        }
                                      >
                                        [{product.number}] {product.name} (
                                        {product.countryCodeOfOrigin}){" "}
                                        {product.version
                                          ? ` | v${product.version}`
                                          : ""}
                                        {product.isActive
                                          ? ""
                                          : " 🔴 - Inactive Product"}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="pt-2">-</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {data ? (
            <div className="p-4 md:p-6 flex justify-end items-center space-x-2 opacity-70">
              {offset > 0 ? (
                <Button
                  prev
                  onClick={() =>
                    setSearchParams({
                      ...params,
                      offset: Math.max(0, offset - PAGE_SIZE),
                    })
                  }
                />
              ) : null}
              <div>
                {total === 0 ? 0 : offset + 1}-
                {Math.min(PAGE_SIZE + offset, total)} / {total}
              </div>
              {total > offset + PAGE_SIZE ? (
                <Button
                  next
                  onClick={() =>
                    setSearchParams({ ...params, offset: PAGE_SIZE + offset })
                  }
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Page>
  );
};

export default LabTestReportsPage;
